/**
 * Table row components for MappingPhase.
 * Extracted from mapping-phase-parts.tsx to keep file sizes within ESLint max-lines.
 *
 * Per-column pattern fan-out (column-mapping-ui spec, "Per-column placeholder
 * patterns with inline fan-out"): a mapping with a 2+-placeholder pattern
 * transform renders indented child rows below the main row, one per
 * placeholder, each with its own MappingFieldPicker. A 1-placeholder pattern
 * stays a single row with a "→ capturedValue" cleaned hint. Warning/match
 * computation always goes through the shared @ooh/contracts pattern engine
 * (see mapping-phase-transform-editor.tsx) — never reimplemented here.
 */
import * as React from 'react';
import { AlertTriangle, Pencil } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { Badge } from '@ooh/ui/components/badge';
import { InfoHint } from '@ooh/ui/components/info-hint';
import { cn } from '@ooh/ui/lib/utils';
import {
  SKIP_VALUE,
  TYP_NOSNIKA_SOURCE_COLUMN,
  RequiredFieldsErrorSection,
} from './mapping-phase-parts';
import type { MappingTransformLabels } from './mapping-phase-transform-editor';
import { computeMappingRowView } from './mapping-row-view';
import type { MappingRowBlankView, MappingRowView } from './mapping-row-view';
import { MappingFieldPicker } from './mapping-field-picker';
import type { FieldOptionItem, MappingPhaseLabels, ColumnMappingItem } from './mapping-phase';

type GroupKey = FieldOptionItem['group'];

interface PatternAffordancesProps {
  mappingId: string;
  hasWarning: boolean;
  /** True for a `valueMap` transform — the only transform type with no other visual cue on
   * the row (a pattern transform already fans out into child rows or shows a "→ captured
   * value" hint, so it doesn't need this badge too). */
  isValueMapped: boolean;
  patternLabels: MappingTransformLabels;
  onEditPattern: (id: string) => void;
}

function PatternAffordances({
  mappingId,
  hasWarning,
  isValueMapped,
  patternLabels,
  onEditPattern,
}: PatternAffordancesProps) {
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="shrink-0 text-muted-foreground hover:text-foreground"
        aria-label={patternLabels.editPatternAriaLabel}
        data-testid={`edit-pattern-${mappingId}`}
        onClick={() => onEditPattern(mappingId)}
      >
        <Pencil className="h-3.5 w-3.5" strokeWidth={1.8} />
      </Button>
      {isValueMapped ? (
        <Badge
          variant="info"
          className="shrink-0 px-1.5 py-0 text-2xs font-medium"
          data-testid={`mapping-value-map-badge-${mappingId}`}
        >
          {patternLabels.transformModeValueMapLabel}
        </Badge>
      ) : null}
      {hasWarning ? (
        <span title={patternLabels.unmatchedSampleWarningLabel}>
          <AlertTriangle
            className="h-4 w-4 text-amber-600 shrink-0"
            data-testid={`mapping-pattern-warning-${mappingId}`}
            aria-label={patternLabels.unmatchedSampleWarningLabel}
          />
        </span>
      ) : null}
    </>
  );
}

interface MappingRowTargetCellProps {
  mapping: ColumnMappingItem;
  view: MappingRowView;
  groupedOptions: { groupKey: GroupKey; label: string; options: FieldOptionItem[] }[];
  skipLabel: string;
  unknownColumnLabel: string;
  fieldSearchPlaceholder: string;
  fieldNoResults: string;
  onSelectChange: (val: string) => void;
  onEditPattern: (id: string) => void;
  patternLabels: MappingTransformLabels;
}

function MappingRowTargetCell({
  mapping,
  view,
  groupedOptions,
  skipLabel,
  unknownColumnLabel,
  fieldSearchPlaceholder,
  fieldNoResults,
  onSelectChange,
  onEditPattern,
  patternLabels,
}: MappingRowTargetCellProps) {
  return (
    <>
      <div className="flex items-center gap-2">
        {view.isFanOut ? (
          <span className="text-xs text-muted-foreground italic">
            {patternLabels.fanOutNoteLabel}
          </span>
        ) : (
          <MappingFieldPicker
            value={view.currentValue === SKIP_VALUE ? null : view.currentValue}
            unknownValueLabel={view.isUnknown ? unknownColumnLabel : undefined}
            groupedOptions={groupedOptions}
            onChange={(val) => onSelectChange(val ?? SKIP_VALUE)}
            labels={{
              skipLabel,
              searchPlaceholder: fieldSearchPlaceholder,
              noResultsLabel: fieldNoResults,
            }}
            testId={`select-mapping-${mapping.id}-trigger`}
          />
        )}
        {!view.isFanOut && view.matchedOption != null ? (
          <InfoHint label={patternLabels.fieldInfoAriaLabel}>
            {view.matchedOption.description}
          </InfoHint>
        ) : null}
        <PatternAffordances
          mappingId={mapping.id}
          hasWarning={view.hasWarning}
          isValueMapped={mapping.transform?.type === 'valueMap'}
          patternLabels={patternLabels}
          onEditPattern={onEditPattern}
        />
      </div>
      {view.isCleaned ? (
        <span className="text-xs text-muted-foreground mt-1 block">
          → {view.cleanedValue || '—'}
        </span>
      ) : null}
    </>
  );
}

interface MappingBlankRowProps {
  mappingId: string;
  blank: MappingRowBlankView;
  groupedOptions: { groupKey: GroupKey; label: string; options: FieldOptionItem[] }[];
  patternLabels: MappingTransformLabels;
  onBlankTargetChange: (id: string, blankIndex: number, value: string | null) => void;
}

function MappingBlankRow({
  mappingId,
  blank,
  groupedOptions,
  patternLabels,
  onBlankTargetChange,
}: MappingBlankRowProps) {
  return (
    <tr
      className="border-b border-border/60 last:border-0 bg-muted/10"
      data-testid={`mapping-blank-row-${mappingId}-${blank.index}`}
    >
      <td className="px-4 py-2 pl-8 font-mono text-xs text-muted-foreground">
        {`{${blank.index}}`} = {blank.capturedValue || '—'}
      </td>
      <td className="px-4 py-2 pr-4">
        <MappingFieldPicker
          value={blank.target}
          groupedOptions={groupedOptions}
          onChange={(value) => onBlankTargetChange(mappingId, blank.index, value)}
          labels={{
            skipLabel: patternLabels.skipLabel,
            searchPlaceholder: patternLabels.fieldSearchPlaceholder ?? '',
            noResultsLabel: patternLabels.fieldNoResults ?? '',
          }}
          testId={`mapping-blank-select-${mappingId}-${blank.index}`}
        />
      </td>
    </tr>
  );
}

export interface MappingTableRowProps {
  mapping: ColumnMappingItem;
  /** 1-based position of this source column in the file — shown next to the column name so
   * operators can tell apart duplicate source-column headers (columnSamples/columnDistinctValues
   * are keyed by column name and would otherwise silently collide for a repeated header). */
  columnNumber: number;
  fieldOptions: FieldOptionItem[];
  groupedOptions: { groupKey: GroupKey; label: string; options: FieldOptionItem[] }[];
  skipLabel: string;
  unknownColumnLabel: string;
  fieldSearchPlaceholder: string;
  fieldNoResults: string;
  onMappingChange: (id: string, value: string) => void;
  /** Sampled raw values for this source column, from the columns API response. */
  sampleValues: string[];
  onEditPattern: (id: string) => void;
  onBlankTargetChange: (id: string, blankIndex: number, value: string | null) => void;
  patternLabels: MappingTransformLabels;
}

export function MappingTableRow({
  mapping,
  columnNumber,
  fieldOptions,
  groupedOptions,
  skipLabel,
  unknownColumnLabel,
  fieldSearchPlaceholder,
  fieldNoResults,
  onMappingChange,
  sampleValues,
  onEditPattern,
  onBlankTargetChange,
  patternLabels,
}: MappingTableRowProps) {
  const view = computeMappingRowView(mapping, fieldOptions, sampleValues);

  function handleSelectChange(val: string) {
    if (view.isCleaned) {
      onBlankTargetChange(
        mapping.id,
        view.singlePlaceholderNumber,
        val === SKIP_VALUE ? null : val,
      );
      return;
    }
    onMappingChange(mapping.id, val);
  }

  return (
    <React.Fragment>
      <tr
        className={cn(
          'border-b border-border/60 last:border-0 hover:bg-muted/20',
          view.isSkipped && 'bg-amber-50/40',
          view.isUnknown && 'bg-amber-50/60',
        )}
        data-testid={`mapping-row-${mapping.id}`}
        data-skipped={view.isSkipped ? 'true' : undefined}
        data-unknown={view.isUnknown ? 'true' : undefined}
      >
        <td className="px-4 py-2.5">
          <div className="flex items-baseline gap-2">
            <span className="shrink-0 font-mono text-xs text-muted-foreground tabular-nums">
              {columnNumber}.
            </span>
            <span className="font-mono text-xs">{mapping.sourceColumn}</span>
          </div>
        </td>
        <td className="px-4 py-2 pr-4">
          <MappingRowTargetCell
            mapping={mapping}
            view={view}
            groupedOptions={groupedOptions}
            skipLabel={skipLabel}
            unknownColumnLabel={unknownColumnLabel}
            fieldSearchPlaceholder={fieldSearchPlaceholder}
            fieldNoResults={fieldNoResults}
            onSelectChange={handleSelectChange}
            onEditPattern={onEditPattern}
            patternLabels={patternLabels}
          />
        </td>
      </tr>
      {view.blanks.map((blank) => (
        <MappingBlankRow
          key={blank.index}
          mappingId={mapping.id}
          blank={blank}
          groupedOptions={groupedOptions}
          patternLabels={patternLabels}
          onBlankTargetChange={onBlankTargetChange}
        />
      ))}
    </React.Fragment>
  );
}

interface TypeOverrideAutoRowProps {
  autoNoteLabel: string;
}

/** A static table row shown in place of the "Typ nośnika" supplier column when a file-level override is active. */
export function TypeOverrideAutoRow({ autoNoteLabel }: TypeOverrideAutoRowProps) {
  return (
    <tr
      className="border-b border-border/60 last:border-0 bg-blue-50/40"
      data-testid="type-override-auto-row"
    >
      <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
        {TYP_NOSNIKA_SOURCE_COLUMN}
      </td>
      <td className="px-4 py-2 pr-4 text-xs text-blue-700 italic">{autoNoteLabel}</td>
    </tr>
  );
}

// Missing-required-field handling (fields entirely absent from the file) moved to
// MissingRequiredFieldsSection in mapping-phase-constant-editor.tsx — those fields
// are now resolvable inline via the constant editor, not just reported as an error.

/** Chip prefix constant for unmapped-required-field error chips. */
export const UNMAPPED_CHIP_PREFIX = 'unmapped-required-chip';

interface MappingRequiredSectionsProps {
  unmappedRequiredFields: FieldOptionItem[];
  labels: Pick<MappingPhaseLabels, 'unmappedRequiredHeading' | 'unmappedRequiredHint'>;
}

export function MappingRequiredSections({
  unmappedRequiredFields,
  labels,
}: MappingRequiredSectionsProps) {
  if (unmappedRequiredFields.length === 0) return null;
  return (
    <RequiredFieldsErrorSection
      icon={<AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />}
      heading={labels.unmappedRequiredHeading}
      hint={labels.unmappedRequiredHint}
      fields={unmappedRequiredFields}
      testId="unmapped-required-section"
      chipTestIdPrefix={UNMAPPED_CHIP_PREFIX}
    />
  );
}
